import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Pencil, Plus, Star, Trash2, MessageSquareQuote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";

const EMPTY = { author_name: "", rating: 5, text: "", source: "Google", review_date: "", order: 0, published: true };

const StarPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1.5 mt-1.5" data-testid="review-rating-picker">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-[2px]"
        aria-label={`${n} star${n > 1 ? "s" : ""}`}
        data-testid={`review-star-${n}`}
      >
        <Star className={`h-6 w-6 transition-colors ${n <= value ? "text-[hsl(var(--kerrys-sun))] fill-[hsl(var(--kerrys-sun))]" : "text-muted-foreground/40"}`} />
      </button>
    ))}
  </div>
);

const StarsRow = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < rating ? "text-[hsl(var(--kerrys-sun))] fill-[hsl(var(--kerrys-sun))]" : "text-muted-foreground/30"}`} />
    ))}
  </div>
);

export default function AdminReviews() {
  const [reviews, setReviews] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // review id or null (new)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    api
      .get("/admin/reviews")
      .then((res) => setReviews(res.data))
      .catch(() => {
        setReviews([]);
        toast.error("Couldn't load reviews");
      });
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r.id);
    setForm({ ...EMPTY, ...r });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.author_name.trim()) {
      toast.error("Please add the reviewer's name");
      return;
    }
    if (!form.text.trim()) {
      toast.error("Please add the review text");
      return;
    }
    setSaving(true);
    const payload = {
      author_name: form.author_name,
      rating: Number(form.rating) || 5,
      text: form.text,
      source: form.source || "Google",
      review_date: form.review_date || "",
      order: Number(form.order) || 0,
      published: !!form.published,
    };
    try {
      if (editing) {
        await api.put(`/reviews/${editing}`, payload);
        toast.success("Review updated");
      } else {
        await api.post("/reviews", payload);
        toast.success("Review added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save review");
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (r) => {
    try {
      await api.put(`/reviews/${r.id}`, { ...r, published: !r.published });
      setReviews((list) => list.map((x) => (x.id === r.id ? { ...x, published: !x.published } : x)));
    } catch {
      toast.error("Couldn't update review");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/reviews/${deleting.id}`);
      toast.success("Review deleted");
      setDeleting(null);
      load();
    } catch {
      toast.error("Couldn't delete review");
    }
  };

  return (
    <div data-testid="admin-reviews-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Reviews</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Paste your best real Google reviews here. Published reviews show on your homepage and the ad landing page.
            Tip: keep the wording exactly as the parent wrote it.
          </p>
        </div>
        <Button onClick={openNew} className="rounded-[4px] font-semibold shrink-0" data-testid="admin-review-add-button">
          <Plus className="h-4 w-4 mr-1.5" /> Add review
        </Button>
      </div>

      <div className="mt-6 space-y-4">
        {reviews === null ? (
          [...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 rounded-[4px]" />)
        ) : reviews.length === 0 ? (
          <Card className="rounded-[4px]">
            <CardContent className="p-12 text-center" data-testid="admin-reviews-empty">
              <MessageSquareQuote className="h-10 w-10 mx-auto text-muted-foreground" />
              <p className="font-display text-2xl font-semibold mt-4">No reviews yet</p>
              <p className="text-muted-foreground text-sm mt-2 max-w-sm mx-auto">
                Copy a lovely review from your Google Business Profile and paste it in — it&rsquo;ll appear on your
                website straight away. The reviews section stays hidden on the site until you add your first one.
              </p>
              <Button onClick={openNew} className="rounded-[4px] font-semibold mt-6" data-testid="admin-review-add-empty-button">
                <Plus className="h-4 w-4 mr-1.5" /> Add your first review
              </Button>
            </CardContent>
          </Card>
        ) : (
          reviews.map((r) => (
            <Card key={r.id} className={`rounded-[4px] ${!r.published ? "opacity-60" : ""}`} data-testid="admin-review-card">
              <CardContent className="p-5 sm:p-6">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <StarsRow rating={r.rating} />
                      <Badge variant="outline" className="rounded-[4px] font-medium">{r.source || "Google"}</Badge>
                      {r.review_date && <span className="text-xs text-muted-foreground">{r.review_date}</span>}
                      {!r.published && (
                        <Badge variant="outline" className="rounded-[4px] bg-secondary text-muted-foreground">Hidden</Badge>
                      )}
                    </div>
                    <p className="mt-3 text-foreground/90 leading-relaxed">&ldquo;{r.text}&rdquo;</p>
                    <p className="mt-3 font-bold text-sm" data-testid="admin-review-author">{r.author_name}</p>
                  </div>
                  <div className="flex md:flex-col gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => togglePublished(r)} className="rounded-[4px]" data-testid="admin-review-toggle-button">
                      {r.published ? <><EyeOff className="h-4 w-4 mr-1.5" /> Hide</> : <><Eye className="h-4 w-4 mr-1.5" /> Show</>}
                    </Button>
                    <Button size="sm" onClick={() => openEdit(r)} className="rounded-[4px] font-semibold" data-testid="admin-review-edit-button">
                      <Pencil className="h-4 w-4 mr-1.5" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(r)} className="rounded-[4px] text-destructive hover:text-destructive" data-testid="admin-review-delete-button">
                      <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit review" : "Add a review"}</DialogTitle>
            <DialogDescription>Paste the parent&rsquo;s review exactly as it appears on Google.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="r-name">Reviewer name</Label>
              <Input id="r-name" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} placeholder="e.g. Sarah J." className="rounded-[4px] mt-1.5" data-testid="admin-review-input-name" />
            </div>
            <div>
              <Label>Rating</Label>
              <StarPicker value={Number(form.rating)} onChange={(n) => setForm({ ...form, rating: n })} />
            </div>
            <div>
              <Label htmlFor="r-text">Review text</Label>
              <Textarea id="r-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={4} placeholder="Paste the review here…" className="rounded-[4px] mt-1.5" data-testid="admin-review-input-text" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="r-source">Source</Label>
                <Input id="r-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Google" className="rounded-[4px] mt-1.5" data-testid="admin-review-input-source" />
              </div>
              <div>
                <Label htmlFor="r-date">Date (optional)</Label>
                <Input id="r-date" value={form.review_date} onChange={(e) => setForm({ ...form, review_date: e.target.value })} placeholder="e.g. March 2025" className="rounded-[4px] mt-1.5" data-testid="admin-review-input-date" />
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="r-order">Sort order</Label>
                <Input id="r-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="rounded-[4px] mt-1.5 w-24" data-testid="admin-review-input-order" />
                <p className="text-xs text-muted-foreground mt-1">Lower shows first</p>
              </div>
              <div className="flex items-center gap-3">
                <Label htmlFor="r-pub" className="cursor-pointer">Published</Label>
                <Switch id="r-pub" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="admin-review-switch-published" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-[4px]">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-[4px] font-semibold" data-testid="admin-review-save-button">
              {saving ? "Saving…" : editing ? "Save changes" : "Add review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this review?</AlertDialogTitle>
            <AlertDialogDescription>The review from {deleting?.author_name} will be permanently removed from your site.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[4px]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-[4px] bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-review-confirm-delete-button">
              Delete review
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
